/* eslint react-hooks/preserve-manual-memoization: ["warn"] */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import useFlag, { FLAGS } from "../../../hooks/useFlag";
import { useArticlesByGroupId } from "../hooks/useArticlesByGroupId";
import { ArticleViewSkeleton } from "../Skeletons/ArticleViewSkeleton";
import { useInputPopup } from "../../InputPopup";
import { Trans, useTranslation } from "@coworker/locales";
import {
  ArticleListItemMobile,
  CenterContainer,
  CenterContainerWithGap,
  ListWrapper,
  SizedText,
} from "../styles/styles";
import FullScreenPopup from "@coworker/apprestructured/src/layout/components/FullScreenPopup/FullScreenPopup";
import { FullWidthButton } from "../../SkapaButton";
import { ModalTypes } from "../types/views";
import { useQueryClient } from "@tanstack/react-query";
import FixaSSRIcon from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/FixaSSRIcon";
import PencilIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/PencilIconPath";
import { GroupActionsModal } from "../Organisms/Modals/GroupActionModal";
import { Group } from "../types/groups";
import { EditGroupModal } from "../Organisms/Modals/EditGroupModal";
import { useGroupsByMediaId } from "../hooks/useGroupsByMediaId";
import { DeleteGroupModal } from "../Organisms/Modals/DeleteGroupModal";
import { QueryKeys } from "../hooks/queryKeys";
import Linkbar from "@coworker/apprestructured/src/shared/components/Linkbar/Linkbar";
import LinkbarLoading from "@coworker/apprestructured/src/shared/components/Linkbar/LinkbarLoading";
import { useMediaById } from "../hooks/useMediaById";
import { sortArticlesByItemName } from "../../../helpers/sorters";
import { useTasksByMedia } from "../hooks/useTasksByMedia";
import { joinProductArticleIds } from "../tools";
import { useEDSData } from "../hooks/useEDSData";
import { useSalesStopData } from "../hooks/useSalesStopData";
import {
  useCountryId,
  useStoreId,
} from "@coworker/app/src/core/auth/useLoggedInUser";
import SimpleArticleCardSkeleton from "@coworker/apprestructured/src/shared/simple/SimpleSkeleton/SimpleArticleCardSkeleton";
import { useToastNotification } from "@coworker/components/src/components/ToastNotification";
import {
  isLocalCombinationArticle,
  processArticles,
  selectedArticleIdsToArticles,
} from "../Helpers/articleHelpers";
import { ArticleType } from "@coworker/types/lib/tasks/constants";
import { LocalCombinationItemsListView } from "@coworker/apprestructured/src/media-maintenance/components/LocalCombinations/LocalCombinationItemsListView/LocalCombinationItemsListView";
import { ItemListViewItem } from "@coworker/apprestructured/src/media-maintenance/components/ItemListViewItem/ItemListViewItem";
import { ItemCardRenderType } from "@coworker/apprestructured/src/shared/types/itemCardRenderType";
import { MediaItemViewType } from "@coworker/apprestructured/src/media-maintenance/types/mediaItemViewType";
import styled from "styled-components";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { SelectArticles } from "../Molecules/SelectArticles";
import { ArticleIdNbrArticles, Articles } from "../types/article";
import { selectArticleIdNbrArticles } from "../Molecules/MediaArticles.helper";
import { PleaseFillIn } from "../../Reusable/PleaseFillIn";
import { MediaArticlesModals } from "../Organisms/Modals/MediaArticlesModals";
import { MediaResponse } from "../types/media";

const SelectButtonContainer = styled("div")`
  padding: 16px;
  text-align: center;
  position: sticky;
  top: 56px;
  background-color: white;
  z-index: 1;
`;

export function GroupView() {
  const { groupId } = useParams();
  const [queryParams] = useSearchParams();
  const { t } = useTranslation();
  const { getInput } = useInputPopup();
  const queryClient = useQueryClient();
  const { showToast } = useToastNotification();
  const navigate = useNavigate();

  const mediaId = queryParams.get("mediaId") ?? undefined;

  const { data: articles } = useArticlesByGroupId(groupId);
  const { data: groups } = useGroupsByMediaId(mediaId);
  const { data: mediaResponse } = useMediaById(mediaId);
  const articleNumbers = joinProductArticleIds(articles ?? []);
  const storeId = useStoreId();
  const countryId = useCountryId();
  const { data: edsData } = useEDSData(countryId, articleNumbers);
  const { data: salesStopData } = useSalesStopData(storeId, articleNumbers);
  const { taskLinks } = useTasksByMedia(mediaId);

  const [currentModalType, setCurrentModalType] = useState<ModalTypes>(
    ModalTypes.DEFAULT_VIEW
  );

  const [isAddingArticles, setIsAddingArticles] = useState(false);
  const [isSelectArticlesMode, setIsSelectArticlesMode] = useState(false);
  const [selectedArticleIdsNbrArticles, setSelectedArticleIdsNbrArticles] =
    useState<ArticleIdNbrArticles[]>([]);
  const [warningText, setWarningText] = useState("");

  const [selectedArticles, setSelectedArticles] = useState<Articles>([]);

  const group = groups?.find((group) => group.id === groupId);

  const previousPath = useMemo(
    () => `/mediamaintenance/tabs/${mediaResponse?.media.id}?tab=groups-tab`,
    [mediaResponse?.media.id]
  );

  const linkbar = useMemo(() => {
    if (
      !group?.name ||
      !mediaResponse?.media.id ||
      !mediaResponse?.media.name
    ) {
      return <LinkbarLoading />;
    }

    return (
      <Linkbar
        currentLabel={group.name}
        items={[
          {
            label: t("mediaMaintenanceString"),
            to: "/mediamaintenance",
          },
          {
            label: mediaResponse.media.name,
            to: previousPath,
          },
        ]}
      />
    );
  }, [group?.name, mediaResponse?.media.id, mediaResponse?.media.name, t]);

  const fullScreenPopup = (children: React.ReactNode) => (
    <FullScreenPopup
      appBarConfig={{
        title: group?.name ?? t("loadingString"),
        actions: [
          {
            name: "settings",
            icon: <FixaSSRIcon paths={PencilIconPath} />,
            onClick: () => setCurrentModalType(ModalTypes.GROUP_ACTIONS),
            position: "right",
          },
        ],
      }}
      actionBarContent={
        <CenterContainerWithGap $centerText={true}>
          {isSelectArticlesMode ? (
            <>
              <PleaseFillIn
                show={!!warningText}
                alternateWarningTranslation={t(warningText)}
                centerText={true}
              />
              <FullWidthButton
                text={t("continue2String")}
                onClick={async () => {
                  if (selectedArticleIdsNbrArticles.length > 0) {
                    setCurrentModalType(
                      ModalTypes.GROUP_SELECTED_ARTICLES_ACTIONS
                    );
                    setSelectedArticles(
                      selectedArticleIdsToArticles(
                        activeArticles,
                        selectedArticleIdsNbrArticles,
                        Object.values(groupedCombinationArticles).flat()
                      )
                    );
                  } else {
                    setWarningText(t("selectArticlesToContinueString"));
                  }
                }}
                type={"primary"}
              />
            </>
          ) : (
            <FullWidthButton
              type="primary"
              text={t("addArticlesToGroupString")}
              onClick={async () => {
                await getInput("addMultipleArticles", {
                  mediaId,
                  groupId,
                  setIsAddingArticles,
                });
                await queryClient.invalidateQueries({
                  queryKey: [QueryKeys.ArticlesByGroup, { id: groupId }],
                });
              }}
            />
          )}
        </CenterContainerWithGap>
      }
      noPadding
      fullHeightContent
      linkbar={linkbar}
    >
      <>
        {children}
        <GroupActionsModal
          modalVisibility={currentModalType === ModalTypes.GROUP_ACTIONS}
          onTriggerNewModal={(modalType: ModalTypes) => {
            setCurrentModalType(modalType);
          }}
          mediaId={mediaId ?? ""}
          currentGroup={group as Group}
          newGroupEditModal={true}
        />
        <EditGroupModal
          modalVisibility={currentModalType === ModalTypes.EDIT_GROUP_NAME}
          currentGroup={group as Group}
          onTriggerNewModal={(modalType: ModalTypes) => {
            setCurrentModalType(modalType);
            queryClient.invalidateQueries({
              queryKey: [QueryKeys.GroupsByMedia, mediaId],
            });
          }}
          groups={groups || []}
        />
        <DeleteGroupModal
          modalVisibility={currentModalType === ModalTypes.DELETE_GROUP}
          mediaId={mediaId || ""}
          allArticles={articles || []}
          currentGroup={group}
          onTriggerNewModal={(_modalType: ModalTypes) => {
            navigate(previousPath, { replace: true });
          }}
        />
        {currentModalType !== ModalTypes.DEFAULT_VIEW && (
          <MediaArticlesModals
            selectedArticles={selectedArticles}
            allArticles={activeArticles || []}
            currentModalType={currentModalType}
            onModalTypeChange={(modalType: ModalTypes) => {
              if (modalType === ModalTypes.DEFAULT_VIEW) {
                setIsSelectArticlesMode(false);
                setSelectedArticles([]);
                setSelectedArticleIdsNbrArticles([]);
              }
              setWarningText("");
              setCurrentModalType(modalType);
            }}
            groups={groups || []}
            storeId={mediaResponse?.media.storeId ?? ""}
            mediaId={mediaResponse?.media.id ?? ""}
            currentGroup={group as Group}
            media={mediaResponse as MediaResponse}
          />
        )}
      </>
    </FullScreenPopup>
  );

  const articlesWasAdded = useRef(isAddingArticles);
  useEffect(() => {
    if (articlesWasAdded.current && !isAddingArticles) {
      showToast(t("articlesAddedSuccessfullyString"), undefined, undefined, 2);
    }
    articlesWasAdded.current = isAddingArticles;
  }, [isAddingArticles]);

  if (!articles || !groups) {
    return <>{fullScreenPopup(<ArticleViewSkeleton />)}</>;
  }

  if (isAddingArticles) {
    return (
      <FullScreenPopup
        noPadding
        linkbar={linkbar}
        appBarConfig={{
          title: group?.name ?? t("loadingString"),
          actions: [
            {
              name: "settings",
              icon: <FixaSSRIcon paths={PencilIconPath} />,
              onClick: () => {},
              position: "right",
            },
          ],
        }}
      >
        {[...Array(3)].map((_, index) => (
          <SimpleArticleCardSkeleton key={index} />
        ))}
      </FullScreenPopup>
    );
  }

  if (!articles || articles?.length === 0) {
    return (
      <>
        {fullScreenPopup(
          <CenterContainer $centerText={true} style={{ margin: "48px 0px" }}>
            <SizedText $fontSize={20} $bold={true}>
              <Trans>noArticlesInGroupString</Trans>
            </SizedText>
          </CenterContainer>
        )}
      </>
    );
  }

  // Process articles to separate active and combination articles
  const { activeArticles, groupedCombinationArticles } =
    processArticles(articles);

  return (
    <>
      {mediaResponse &&
        fullScreenPopup(
          <>
            <SelectButtonContainer>
              <FixaButton
                type={"secondary"}
                text={
                  isSelectArticlesMode
                    ? t("cancelString")
                    : t("selectArticlesString")
                }
                onClick={() => {
                  setIsSelectArticlesMode((prev) => !prev);
                  setSelectedArticleIdsNbrArticles([]);
                  setWarningText("");
                }}
                fluid
              />
            </SelectButtonContainer>
            <SelectArticles
              selectedArticles={selectedArticleIdsNbrArticles}
              uniqueArticles={activeArticles}
              filteredArticles={activeArticles}
              isSelected={isSelectArticlesMode}
              onSelectAll={(selectAllChecked: boolean) => {
                if (selectAllChecked) {
                  setSelectedArticleIdsNbrArticles(
                    activeArticles?.map((article) => ({
                      articleId: article.id,
                      nbrArticles: article.nbrArticles,
                      productArticleId: article.productArticleId,
                    })) ?? []
                  );
                } else {
                  setSelectedArticleIdsNbrArticles([]);
                }
              }}
            />
            <ListWrapper>
              {activeArticles.sort(sortArticlesByItemName).map((article) => (
                <>
                  <ArticleListItemMobile key={article.id}>
                    <ItemListViewItem
                      article={article}
                      itemCardRenderType={ItemCardRenderType.link}
                      media={mediaResponse?.media}
                      taskLinks={taskLinks}
                      edsData={
                        edsData?.find(
                          (eds: { productArticleId: string }) =>
                            eds.productArticleId === article.productArticleId
                        ) ?? null
                      }
                      salesStopData={
                        salesStopData?.find(
                          (salesStop) =>
                            salesStop.productArticleId ===
                            article.productArticleId
                        ) ?? null
                      }
                      selectArticleIdsMode={isSelectArticlesMode}
                      handleSelectedItems={(articleIdNbrArticles) =>
                        selectArticleIdNbrArticles(
                          articleIdNbrArticles,
                          selectedArticleIdsNbrArticles,
                          setSelectedArticleIdsNbrArticles
                        )
                      }
                      isSelected={
                        !!selectedArticleIdsNbrArticles.find(
                          (item) => item.articleId === article.id
                        )
                      }
                    />
                  </ArticleListItemMobile>
                  {isLocalCombinationArticle(
                    article.productArticleType as ArticleType
                  ) && (
                    <LocalCombinationItemsListView
                      parentArticle={article}
                      combinationArticles={groupedCombinationArticles}
                      edsData={edsData ?? null}
                      salesStopData={salesStopData}
                      mediaItemViewType={MediaItemViewType.SIMPLE_ITEM_VIEW}
                    />
                  )}
                </>
              ))}
            </ListWrapper>
          </>
        )}
    </>
  );
}
